import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useSpring, useMotionTemplate, useReducedMotion } from "framer-motion";

const DEFAULT_ROTATION = [
  { image: "/products/magforte.jpg", alt: "Mag Forte D3 · Magnesium + Vitamin D3" },
  { image: "/products/physiomoveflex.jpg", alt: "PhysiomoveFlex — Joint & Mobility Collagen" },
  { image: "/products/physiofuel.jpg", alt: "PhysioFuel AM-PM+ 3-Phase Recovery System" },
  { image: "/products/variflux.jpg", alt: "VariFlux — Healthy Veins & Vessels" },
  { image: "/products/b12spray.jpg", alt: "Vitamin B12 Spray 1500 mcg" },
];

/**
 * Premium 3D hero product showcase for Physio Essential.
 * Cycles through flagship products every ~6 seconds with a slow crossfade.
 */
export default function Hero3D({ containerRef, products = DEFAULT_ROTATION, intervalMs = 6000 }) {
  const prefersReducedMotion = useReducedMotion();
  const cardRef = useRef(null);
  const [isCoarse, setIsCoarse] = useState(false);
  const [index, setIndex] = useState(0);
  const current = products[index % products.length];

  // Rotate flagship products
  useEffect(() => {
    if (products.length < 2) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % products.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [products.length, intervalMs]);

  useEffect(() => {
    const mq = window.matchMedia("(pointer: coarse)");
    const update = () => setIsCoarse(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  // Mouse-follow (spring-smoothed, more restrained — ±5°)
  const rotateX = useSpring(0, { stiffness: 55, damping: 22, mass: 0.8 });
  const rotateY = useSpring(0, { stiffness: 55, damping: 22, mass: 0.8 });
  const parallaxX = useSpring(0, { stiffness: 45, damping: 24, mass: 0.7 });
  const parallaxY = useSpring(0, { stiffness: 45, damping: 24, mass: 0.7 });
  // Light source position (0-100%) drives specular sheen + rim light
  const lightX = useSpring(50, { stiffness: 45, damping: 22 });
  const lightY = useSpring(30, { stiffness: 45, damping: 22 });
  // Shadow shifts opposite to tilt
  const shadowX = useSpring(0, { stiffness: 40, damping: 22 });
  const shadowSquish = useSpring(1, { stiffness: 40, damping: 22 });

  useEffect(() => {
    if (prefersReducedMotion || isCoarse) return;
    const el = cardRef.current;
    if (!el) return;
    const handle = (e) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / rect.width;
      const dy = (e.clientY - cy) / rect.height;
      rotateY.set(dx * 5);
      rotateX.set(-dy * 5);
      parallaxX.set(dx * 14);
      parallaxY.set(dy * 10);
      // Cursor position → 20–80% inside the specular gradient
      lightX.set(50 + dx * 30);
      lightY.set(30 + dy * 20);
      shadowX.set(-dx * 30);
      shadowSquish.set(1 - Math.abs(dy) * 0.15);
    };
    const reset = () => {
      rotateX.set(0);
      rotateY.set(0);
      parallaxX.set(0);
      parallaxY.set(0);
      lightX.set(50);
      lightY.set(30);
      shadowX.set(0);
      shadowSquish.set(1);
    };
    window.addEventListener("mousemove", handle);
    window.addEventListener("mouseleave", reset);
    return () => {
      window.removeEventListener("mousemove", handle);
      window.removeEventListener("mouseleave", reset);
    };
  }, [isCoarse, prefersReducedMotion, rotateX, rotateY, parallaxX, parallaxY, lightX, lightY, shadowX, shadowSquish]);

  // Specular sheen background — a soft ellipse that moves with the "light"
  const sheenBg = useMotionTemplate`radial-gradient(ellipse 55% 45% at ${lightX}% ${lightY}%, rgba(255,255,255,0.28), rgba(255,255,255,0) 65%)`;
  // Rim-light — a thin bright edge on the "lit" side of the pack
  const rimBg = useMotionTemplate`radial-gradient(ellipse 75% 65% at ${lightX}% ${lightY}%, rgba(52,216,160,0.35), rgba(52,216,160,0) 55%)`;
  // Dynamic contact-shadow horizontal shift
  const shadowTransform = useMotionTemplate`translate(-50%, 0) translateX(${shadowX}px) scaleX(${shadowSquish})`;

  // Scroll-linked cinematic transforms
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });
  const scrollZoom = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const scrollRotate = useTransform(scrollYProgress, [0, 1], [0, 14]);
  const scrollLift = useTransform(scrollYProgress, [0, 1], [0, -60]);
  // Backdrop focus-pull: gently blurs as you scroll away
  const bgBlurPx = useTransform(scrollYProgress, [0, 1], [0, 6]);
  const bgBlur = useMotionTemplate`blur(${bgBlurPx}px)`;
  const bgFarY = useTransform(scrollYProgress, [0, 1], [0, 40]);
  const bgNearY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const bgFade = useTransform(scrollYProgress, [0, 0.9], [0.9, 0.28]);

  return (
    <div
      ref={cardRef}
      className="pointer-events-none relative flex h-full min-h-[480px] items-center justify-center"
      style={{ perspective: 1600 }}
      data-testid="hero-3d-showcase"
    >
      {/* CLINICAL BACKDROP — split into far & near depth layers */}
      <motion.div
        style={{ opacity: bgFade, filter: bgBlur }}
        className="absolute inset-0"
        aria-hidden="true"
      >
        {/* FAR layer — deep atmospheric orbs, slowest parallax */}
        <motion.div
          style={{ y: bgFarY }}
          className="absolute inset-0 flex scale-[0.55] items-center justify-center sm:scale-75 lg:scale-100"
        >
          <div className="absolute h-[620px] w-[620px] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(47,128,237,0.14),transparent_65%)] blur-3xl" />
          <div className="absolute h-[520px] w-[520px] rounded-full border border-[#34D8A0]/12" />
          <div className="absolute h-[420px] w-[420px] rounded-full border border-[#34D8A0]/18" />
          <div className="absolute h-[320px] w-[320px] rounded-full border border-white/8" />
          <div className="absolute h-[240px] w-[240px] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(52,216,160,0.14),transparent_70%)]" />
        </motion.div>

        {/* NEAR layer — molecular ring, nerve paths, knee arc, faster parallax */}
        <motion.div
          style={{ y: bgNearY }}
          className="absolute inset-0 flex scale-[0.55] items-center justify-center sm:scale-75 lg:scale-100"
        >
          {/* Slow rotating molecular ring */}
          <motion.svg
            viewBox="0 0 600 600"
            className="absolute h-[600px] w-[600px] opacity-[0.20]"
            animate={prefersReducedMotion ? {} : { rotate: 360 }}
            transition={{ repeat: Infinity, duration: 110, ease: "linear" }}
          >
            <defs>
              <radialGradient id="node" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#34D8A0" />
                <stop offset="100%" stopColor="#34D8A0" stopOpacity="0" />
              </radialGradient>
            </defs>
            <circle cx="300" cy="300" r="240" fill="none" stroke="#34D8A0" strokeWidth="0.5" strokeDasharray="2 6" />
            {[0, 60, 120, 180, 240, 300].map((deg) => {
              const rad = (deg * Math.PI) / 180;
              const x = 300 + Math.cos(rad) * 240;
              const y = 300 + Math.sin(rad) * 240;
              return <circle key={deg} cx={x} cy={y} r="6" fill="url(#node)" />;
            })}
          </motion.svg>

          {/* Counter-rotating micro-molecule ring (adds subtle depth) */}
          <motion.svg
            viewBox="0 0 600 600"
            className="absolute h-[420px] w-[420px] opacity-[0.14]"
            animate={prefersReducedMotion ? {} : { rotate: -360 }}
            transition={{ repeat: Infinity, duration: 160, ease: "linear" }}
          >
            <circle cx="300" cy="300" r="170" fill="none" stroke="#ffffff" strokeWidth="0.4" strokeDasharray="1 5" />
            {[30, 90, 150, 210, 270, 330].map((deg) => {
              const rad = (deg * Math.PI) / 180;
              const x = 300 + Math.cos(rad) * 170;
              const y = 300 + Math.sin(rad) * 170;
              return <circle key={deg} cx={x} cy={y} r="3.5" fill="#ffffff" opacity="0.7" />;
            })}
          </motion.svg>

          {/* Nerve pathway curves */}
          <svg viewBox="0 0 600 600" className="absolute h-[560px] w-[560px] opacity-[0.14]" aria-hidden="true">
            <path d="M40 300 Q 160 180, 300 260 T 560 200" stroke="#2F80ED" strokeWidth="1" fill="none" strokeLinecap="round" />
            <path d="M40 380 Q 200 460, 320 400 T 560 460" stroke="#34D8A0" strokeWidth="1" fill="none" strokeLinecap="round" />
          </svg>

          {/* Knee-joint / articulating arc */}
          <svg viewBox="0 0 600 600" className="absolute h-[500px] w-[500px] opacity-[0.09]" aria-hidden="true">
            <path d="M220 120 C 260 220, 260 380, 220 480" stroke="#ffffff" strokeWidth="1.2" fill="none" strokeLinecap="round" />
            <path d="M380 120 C 340 220, 340 380, 380 480" stroke="#ffffff" strokeWidth="1.2" fill="none" strokeLinecap="round" />
            {[180, 260, 340, 420].map((y) => (
              <line key={y} x1="230" y1={y} x2="370" y2={y} stroke="#ffffff" strokeWidth="0.8" strokeDasharray="3 5" />
            ))}
          </svg>

          {/* Instrument-style measurement ticks on the right edge */}
          <svg viewBox="0 0 600 600" className="absolute h-[560px] w-[560px] opacity-[0.16]" aria-hidden="true">
            {Array.from({ length: 22 }).map((_, i) => {
              const y = 60 + i * 22;
              const long = i % 4 === 0;
              return (
                <line
                  key={i}
                  x1={long ? 540 : 552}
                  y1={y}
                  x2={565}
                  y2={y}
                  stroke="#34D8A0"
                  strokeWidth={long ? 0.9 : 0.5}
                />
              );
            })}
          </svg>
        </motion.div>
      </motion.div>

      {/* CONTACT SHADOW — physically shifts with tilt */}
      <motion.div
        aria-hidden="true"
        style={{ transform: shadowTransform }}
        className="pointer-events-none absolute bottom-[14%] left-1/2 h-16 w-[60%] rounded-[50%] bg-black/55 blur-2xl"
      />

      {/* PRODUCT stack — tilt/parallax/scroll */}
      <motion.div
        style={{
          rotateX: prefersReducedMotion ? 0 : rotateX,
          rotateY: prefersReducedMotion ? 0 : rotateY,
          x: prefersReducedMotion ? 0 : parallaxX,
          y: prefersReducedMotion ? 0 : parallaxY,
          scale: scrollZoom,
          transformStyle: "preserve-3d",
        }}
        className="relative"
      >
        <motion.div
          style={{ y: scrollLift, rotate: scrollRotate, transformStyle: "preserve-3d" }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              animate={prefersReducedMotion ? {} : { y: [0, -8, 0], rotate: [-0.5, 0.5, -0.5] }}
              transition={{
                y: { repeat: Infinity, duration: 7, ease: "easeInOut" },
                rotate: { repeat: Infinity, duration: 11, ease: "easeInOut" },
              }}
              className="relative"
            >
              {/* Rim light — sits BEHIND the product, only visible around its silhouette */}
              <motion.div
                aria-hidden="true"
                style={{
                  backgroundImage: rimBg,
                  WebkitMaskImage:
                    "radial-gradient(ellipse 60% 65% at center, transparent 42%, #000 55%, transparent 92%)",
                  maskImage:
                    "radial-gradient(ellipse 60% 65% at center, transparent 42%, #000 55%, transparent 92%)",
                }}
                className="pointer-events-none absolute inset-0 z-[5]"
              />

              {/* PRODUCT image — cycles through flagships with slow crossfade */}
              <div className="relative z-10 mx-auto h-[340px] w-[300px] sm:h-[420px] sm:w-[380px] lg:h-[500px] lg:w-[460px]">
                <AnimatePresence mode="sync">
                  <motion.img
                    key={current.image}
                    src={current.image}
                    alt={current.alt}
                    initial={{ opacity: 0, scale: 0.94, filter: "blur(6px)" }}
                    animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                    exit={{ opacity: 0, scale: 0.96, filter: "blur(6px)" }}
                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 h-full w-full object-contain drop-shadow-[0_55px_75px_rgba(0,0,0,0.6)] drop-shadow-[0_25px_35px_rgba(52,216,160,0.15)]"
                    style={{
                      WebkitMaskImage:
                        "radial-gradient(ellipse 55% 62% at center, #000 40%, rgba(0,0,0,0.6) 70%, transparent 92%)",
                      maskImage:
                        "radial-gradient(ellipse 55% 62% at center, #000 40%, rgba(0,0,0,0.6) 70%, transparent 92%)",
                    }}
                    loading="eager"
                    fetchpriority="high"
                    draggable={false}
                  />
                </AnimatePresence>
                {/* Dynamic specular sheen — sits ON the product, tracks the mouse */}
                <motion.div
                  aria-hidden="true"
                  style={{
                    backgroundImage: sheenBg,
                    WebkitMaskImage:
                      "radial-gradient(ellipse 45% 55% at center, #000 30%, rgba(0,0,0,0.7) 55%, transparent 85%)",
                    maskImage:
                      "radial-gradient(ellipse 45% 55% at center, #000 30%, rgba(0,0,0,0.7) 55%, transparent 85%)",
                    mixBlendMode: "screen",
                  }}
                  className="pointer-events-none absolute inset-0"
                />
                {/* Very subtle grain for material realism */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-overlay"
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.5 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
                    backgroundSize: "120px 120px",
                    WebkitMaskImage:
                      "radial-gradient(ellipse 50% 60% at center, #000 40%, transparent 85%)",
                    maskImage:
                      "radial-gradient(ellipse 50% 60% at center, #000 40%, transparent 85%)",
                  }}
                />
              </div>

              {/* MIRROR REFLECTION — flipped, gradient-masked, feels like a polished plinth */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-full mt-2 h-[130px] w-[80%] -translate-x-1/2 overflow-hidden opacity-30"
                style={{
                  WebkitMaskImage:
                    "linear-gradient(to bottom, #000 0%, rgba(0,0,0,0.5) 40%, transparent 92%)",
                  maskImage:
                    "linear-gradient(to bottom, #000 0%, rgba(0,0,0,0.5) 40%, transparent 92%)",
                }}
              >
                <AnimatePresence mode="sync">
                  <motion.img
                    key={current.image}
                    src={current.image}
                    alt=""
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 mx-auto h-full w-full -scale-y-100 object-contain blur-[2px]"
                    draggable={false}
                    style={{
                      WebkitMaskImage:
                        "radial-gradient(ellipse 55% 62% at center, #000 40%, rgba(0,0,0,0.6) 70%, transparent 92%)",
                      maskImage:
                        "radial-gradient(ellipse 55% 62% at center, #000 40%, rgba(0,0,0,0.6) 70%, transparent 92%)",
                    }}
                  />
                </AnimatePresence>
              </div>

              {/* Soft ground light beneath */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-full h-24 w-[60%] -translate-x-1/2 -translate-y-6 rounded-[50%] bg-[radial-gradient(ellipse_at_center,rgba(52,216,160,0.3),transparent_70%)] blur-2xl"
              />
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Deep vignette + faint edge fog for cinematic feel */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_50%,rgba(10,37,64,0.6)_100%)]" />
    </div>
  );
}
